import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Copy, QrCode, Share2, Check, ExternalLink, Rocket } from 'lucide-preact';
import QRCode from 'qrcode';
import { logShareEvent, supabaseAvailable } from '../lib/supabase-bridge';
import { getDeviceId } from '../lib/deviceId';
import { buildShareText, canonicalShareUrl, channelShareUrl } from '../lib/share-urls';
import { t } from '../lib/i18n';

type ShareMethod = 'web_share' | 'clipboard' | 'qr' | 'x' | 'whatsapp';

interface ShareCardProps {
  appName: string;
  appDescription: string;
  shareUrl: string;
  /** Stable per-app identifier — used to scope the "first 5 users" share
   *  counter so each new app gets its own viral coachmark. */
  appId?: string;
  /** §virality-P0 — the auto-generated OG image; previewed on the share card
   *  so the creator sees the exact social card their link will unfurl into. */
  ogImageUrl?: string;
  /** The one sentence that built it — the "I made this from a sentence" proof. */
  prompt?: string;
  /** §S679 — the app's share identity. When BOTH are present the card copies /
   *  shares the canonical `/r/<ownerHandle>/<slug>` deep link instead of the raw
   *  preview URL. Pass these only for PUBLIC apps: the /r/ resolver 404s on
   *  private ones (deanonymization guard), so a private app's honest link is
   *  its preview URL (the `shareUrl` fallback). */
  slug?: string | null;
  ownerHandle?: string | null;
  onShared?: (method: ShareMethod) => void;
}

const FIRST_FIVE_TARGET = 5;
const shareStoreKey = (appId: string | undefined) =>
  appId ? `pomela:share-count:${appId}` : null;

// §S680 audit fix — the "first 5 users" meter counts only OUTBOUND share
// intents (a native share sheet completing, a channel composer opening).
// Revealing the QR or copying the link is useful telemetry
// but is NOT evidence the link reached anyone — those no longer fill dots.
const OUTBOUND_METHODS: ReadonlySet<ShareMethod> = new Set(['web_share', 'x', 'whatsapp']);

function loadShareCount(appId?: string): number {
  try {
    const key = shareStoreKey(appId);
    if (!key) return 0;
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch { return 0; }
}

// Only persist the share count when we have a concrete appId. Previously
// a pre-appId share landed under `pomela:share-count:pending`, which the
// next generation read back as inflated progress — the audit caught a
// repro where back-to-back generations would inherit each other's counts.
function saveShareCount(appId: string | undefined, n: number): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = shareStoreKey(appId);
    if (!key) return;
    localStorage.setItem(key, String(n));
  } catch {}
}

// Viral share surface — the central post-generation CTA. Big "Open"
// button, secondary share row, and a one-tap-reveal QR code so a friend
// can scan from a phone if you're on desktop. Every action fires the
// onShared callback so the parent can dispatch posthog events.
//
// Viral PWA OS update: now leads with "Your app is live. Send it to your
// first 5 users." + a 5-dot progress indicator that fills on OUTBOUND
// share intents (see OUTBOUND_METHODS). The urgency primitive that turns
// one creator into 5 visitors.
export function ShareCard({ appName, appDescription, shareUrl, appId, ogImageUrl, prompt, slug, ownerHandle, onShared }: ShareCardProps) {
  const canWebShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [shareCount, setShareCount] = useState(() => loadShareCount(appId));
  const [ogBroken, setOgBroken] = useState(false);
  const cancelTimer = useRef<number | null>(null);

  // §virality-P0 — the share text that travels with every channel. Leads with
  // the proof ("built in one sentence with Pomela") so the link is a pitch,
  // not just a URL. The OG card carries the visual; this carries the hook.
  const shareText = useMemo(() => buildShareText(appName), [appName]);

  // §S679 — the ONE link this card copies/shares/encodes: the canonical
  // /r/<ownerHandle>/<slug> identity when the caller supplied one, else the
  // preview URL the card has always shared (back-compat for every existing
  // call site, which passes neither slug nor ownerHandle).
  const canonicalUrl = useMemo(
    () => canonicalShareUrl({ slug, ownerHandle, url: shareUrl, appId: appId ?? '' }),
    [slug, ownerHandle, shareUrl, appId]
  );

  // Re-hydrate the per-app counter whenever the appId changes (a new
  // generation lands). Keeps the "send to 5" coachmark fresh per app.
  useEffect(() => { setShareCount(loadShareCount(appId)); }, [appId]);

  const remaining = Math.max(0, FIRST_FIVE_TARGET - shareCount);
  const filled = Math.min(shareCount, FIRST_FIVE_TARGET);
  const headline = useMemo(() => {
    if (shareCount === 0) return t('share.headline0');
    if (remaining === 0) return t('share.headlineDone');
    return remaining === 1 ? t('share.toGoOne') : t('share.toGoMany', { n: remaining });
  }, [shareCount, remaining]);

  function recordShare(kind: ShareMethod): void {
    // Only outbound intents advance the "first 5" meter (see OUTBOUND_METHODS);
    // QR-reveal and clipboard copies stay telemetry-only.
    if (OUTBOUND_METHODS.has(kind)) {
      setShareCount((prev) => {
        const next = prev + 1;
        saveShareCount(appId, next);
        return next;
      });
    }
    // Fire-and-forget Supabase log (gated by appId + the runtime config).
    // The owner_id falls back to the device id for anonymous-but-tracked
    // attribution; a future Clerk-signed-in user gets a stable identifier
    // upstream. Never blocks the share flow. ALL methods are logged — the
    // meter policy above only affects the visible dots.
    if (appId && supabaseAvailable()) {
      void logShareEvent({
        appId,
        ownerId: getDeviceId(),
        kind
      });
    }
  }

  useEffect(() => {
    if (!showQr || qrSvg) return;
    void QRCode.toString(canonicalUrl, {
      type: 'svg',
      margin: 1,
      color: { dark: '#0A0A0A', light: '#FFFFFF' },
      errorCorrectionLevel: 'M'
    })
      .then((svg) => setQrSvg(svg))
      .catch(() => setQrSvg(null));
  }, [showQr, canonicalUrl, qrSvg]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      recordShare('clipboard');
      onShared?.('clipboard');
      if (cancelTimer.current) window.clearTimeout(cancelTimer.current);
      cancelTimer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Older Safari without clipboard API — fall through to selection.
      const el = document.createElement('textarea');
      el.value = canonicalUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      recordShare('clipboard');
      onShared?.('clipboard');
    }
  }

  async function webShare() {
    if (!canWebShare) {
      void copyLink();
      return;
    }
    try {
      // §virality-P0 — lead with the proud hook + the app; the OG card unfurls
      // the visual. `appDescription` rides along as a secondary line.
      await navigator.share({ title: appName, text: `${shareText}\n${appDescription}`, url: canonicalUrl });
      recordShare('web_share');
      onShared?.('web_share');
    } catch {
      // user cancelled — silent
    }
  }

  // §virality-P0 — per-channel deep links. Each opens the native composer
  // pre-filled with the proud text + the link; the OG image unfurls on render.
  function shareTo(channel: 'x' | 'whatsapp'): void {
    window.open(channelShareUrl(channel, shareText, canonicalUrl), '_blank', 'noopener,noreferrer');
    recordShare(channel);
    onShared?.(channel);
  }

  function openLive() {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="surface flex flex-col gap-3 p-4 animate-vs-slide-up">
      {/* §virality-P0 — the pride moment. The exact OG card the link unfurls
          into + the one-sentence proof. This is the high-intent surface; make
          it feel like an achievement, not a utility. */}
      {ogImageUrl && !ogBroken && (
        <div
          className="overflow-hidden rounded-2xl border border-[color:var(--border)]"
          style={{ aspectRatio: '1200 / 630', background: 'var(--bg-subtle)' }}
        >
          <img
            src={ogImageUrl}
            alt={t('share.ogAlt', { name: appName })}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setOgBroken(true)}
          />
        </div>
      )}
      {prompt && (
        <p className="text-[12px] leading-snug text-[color:var(--fg-muted)]">
          <span aria-hidden="true">✨ </span>{t('share.builtPrefix')} <strong className="text-[color:var(--fg)]">{appName}</strong>{' '}
          {t('share.builtSuffix')} <span className="italic">“{prompt.length > 90 ? prompt.slice(0, 88) + '…' : prompt}”</span>
        </p>
      )}

      {/* Viral coachmark — "Send it to your first 5 users." Tightens
          the post-deploy distribution loop. Fills in with each share. */}
      <div className="flex items-center gap-3" data-share-coach>
        <span
          aria-hidden="true"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
          style={{
            background: 'var(--gradient-brand)',
            color: '#fff',
            boxShadow: '0 6px 18px -6px rgba(244, 71, 126, 0.55)'
          }}
        >
          <Rocket size={16} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight text-[color:var(--fg)]">
            {headline}
          </div>
          <div className="mt-1 flex items-center gap-1" aria-label={t('share.progressAria', { filled, total: FIRST_FIVE_TARGET })}>
            {Array.from({ length: FIRST_FIVE_TARGET }).map((_, i) => (
              <span
                key={i}
                aria-hidden="true"
                className="h-1.5 w-6 rounded-full transition-colors"
                style={{
                  background:
                    i < filled
                      ? 'var(--gradient-brand)'
                      : 'color-mix(in srgb, var(--fg) 8%, transparent)'
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button type="button" onClick={openLive} className="btn btn-primary flex-1">
          <ExternalLink size={16} />
          {t('share.openLive')}
        </button>
        <button
          type="button"
          onClick={webShare}
          className="btn btn-secondary flex-1"
          aria-label={t('share.ariaShare')}
        >
          <Share2 size={16} />
          {canWebShare ? t('share.share') : t('share.shareLink')}
        </button>
      </div>

      {/* §virality-P0 — per-channel share row. Direct deep links so a creator
          posts to the network where their first users already are. Each is
          icon + label, 44px tap target, brand-tinted on hover. */}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => shareTo('x')} className="vs-chan-btn" aria-label={t('share.ariaX')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/></svg>
          <span>X</span>
        </button>
        <button type="button" onClick={() => shareTo('whatsapp')} className="vs-chan-btn" aria-label={t('share.ariaWhatsApp')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-1.045zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
          <span>WhatsApp</span>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={copyLink}
          className="group flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-subtle)] px-3 py-2 text-left transition-colors hover:bg-[color:var(--bg-elevated)]"
          aria-label={t('share.ariaCopy')}
        >
          <span className="truncate text-[12px] font-mono text-[color:var(--fg-muted)]">{prettyUrl(canonicalUrl)}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-[color:var(--fg-subtle)] group-hover:text-[color:var(--fg)]">
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? t('invite.copied') : t('help.copy')}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            setShowQr((v) => !v);
            if (!showQr) {
              // §S680 — revealing the QR is telemetry-only: it is logged
              // (recordShare filters it out of the meter) but does NOT fill
              // the "first 5" dots; only outbound intents advance those.
              recordShare('qr');
              onShared?.('qr');
            }
          }}
          aria-label={t('share.ariaQr')}
          className="btn btn-ghost !h-10 !w-10 !p-0"
          style={{ borderRadius: 12 }}
        >
          <QrCode size={16} />
        </button>
      </div>

      {showQr && (
        <div className="surface flex items-center gap-3 p-3 animate-vs-scale-in">
          <div
            className="grid h-28 w-28 shrink-0 place-items-center overflow-hidden rounded-xl bg-white p-1.5"
            // QR SVG is trusted (we generated it ourselves) — render directly.
            dangerouslySetInnerHTML={qrSvg ? { __html: qrSvg } : undefined}
          >
            {!qrSvg && <span className="text-[11px] text-[color:var(--fg-subtle)]">…</span>}
          </div>
          <div className="min-w-0 flex-1 text-[12px] leading-relaxed text-[color:var(--fg-muted)]">
            {t('share.qrHelp')}
            <p className="mt-1 truncate font-mono text-[11px] text-[color:var(--fg-subtle)]">{prettyUrl(canonicalUrl)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function prettyUrl(url: string): string {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://x');
    return u.host + u.pathname + (u.search.length > 1 ? '?…' : '');
  } catch {
    return url;
  }
}
